import React, { useRef, useState, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { setPreventUnload } from 'flow/actions/app-actions';
import useAppContext from 'flow/hooks/useAppContext';
import BaseButton from 'flow/components/ui-components/BaseButton';
import Icon from 'flow/components/ui-components/Icon';
import Tooltip from 'flow/components/ui-components/Tooltip';
import RangeSlider from 'flow/components/ui-components/RangeSlider';
import { useRowTabIndex } from 'flow/components/ui-components/row-tab-stop';

interface Props {
    url: string;
    duration?: number;
    downloadUrl?: string;
    presentDownload?: boolean;
    onClickDownload?: () => void;
    onClickPlayPause?: (value: 'play' | 'pause') => void;
    /**
     * 'none' unless the player is the only one on screen. Every preloading
     * player fetches its file as it renders, so a list of them sends one
     * request per row to home, which loads the whole mailbox for each
     * voicemail (TEL-8664). The duration comes from `duration`, not from
     * the file.
     */
    preload?: 'metadata' | 'none' | 'auto';
    className?: string;
    /**
     * The id of the element that says what the recording is, such as the
     * caller's name. A screen reader reads it with the play button.
     */
    playDescribedBy?: string;
}

const AudioPlayerV2 = ({
    url,
    duration,
    downloadUrl,
    presentDownload,
    onClickDownload,
    onClickPlayPause,
    preload = 'none',
    className,
    playDescribedBy,
}: Props) => {
    const { i18next } = useAppContext();
    const rowTabIndex = useRowTabIndex();
    const audioRef = useRef<HTMLAudioElement | null>(null);
    // A seek made before the file's metadata has loaded, as with
    // preload="none" before the first Play. It is applied once it loads.
    const pendingSeekRef = useRef<number | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [timeLeft, setTimeLeft] = useState<number | null>(duration || null);
    // Held in state rather than read off audioRef during render, so every
    // render that uses it was caused by the value it describes.
    const [totalDuration, setTotalDuration] = useState(duration || 0);
    const elapsed = totalDuration - (timeLeft ?? totalDuration);

    useEffect(() => {
        setTimeLeft(duration);
        setTotalDuration(duration || 0);
    }, [duration]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return undefined;
        pendingSeekRef.current = null;

        const updateProgress = () => {
            if (Number.isNaN(audio.currentTime) || Number.isNaN(audio.duration))
                return;

            setTimeLeft(audio.duration - audio.currentTime);
        };

        const onLoadedMetadata = () => {
            const pendingSeek = pendingSeekRef.current;
            pendingSeekRef.current = null;
            if (pendingSeek !== null && !Number.isNaN(audio.duration)) {
                audio.currentTime = Math.min(audio.duration, pendingSeek);
            }
            setTimeLeft((audio.duration || 0) - audio.currentTime);
            setTotalDuration(audio.duration || 0);
        };

        const handleAudioEnded = () => {
            setIsPlaying(false);
            setTimeLeft(audio.duration);
            audio.currentTime = 0;
        };

        audio.addEventListener('timeupdate', updateProgress);
        audio.addEventListener('loadedmetadata', onLoadedMetadata);
        audio.addEventListener('ended', handleAudioEnded);

        return () => {
            audio.removeEventListener('timeupdate', updateProgress);
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
            audio.removeEventListener('ended', handleAudioEnded);
        };
    }, [url]);

    const onDownloadHrefClicked = () => {
        setPreventUnload(true);
        onClickDownload?.();
    };

    const togglePlayPause = () => {
        onClickPlayPause?.(isPlaying ? 'pause' : 'play');
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
        } else {
            audio.play();
        }
        setIsPlaying(!isPlaying);
    };

    const seekToSeconds = (seconds: number) => {
        const audio = audioRef.current;
        if (!audio) return;

        if (!Number.isFinite(audio.duration)) {
            // Not loaded yet: show the position against the known duration
            // and seek there once the file loads, rather than fetch it now.
            if (!totalDuration) return;
            const clamped = Math.min(totalDuration, Math.max(0, seconds));
            pendingSeekRef.current = clamped;
            setTimeLeft(totalDuration - clamped);
            return;
        }

        const clamped = Math.min(audio.duration, Math.max(0, seconds));
        audio.currentTime = clamped;
        setTimeLeft(audio.duration - clamped);
    };

    const formatTime = (time?: number) => {
        if (!time) return '';
        const minutes = Math.floor(time / 60)
            .toString()
            .padStart(2, '0');
        const seconds = Math.floor(time % 60)
            .toString()
            .padStart(2, '0');
        return `${minutes}:${seconds}`;
    };

    return (
        <div
            className="flex grow flex-row items-center gap-1"
            dir="ltr"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
        >
            <div
                className={twMerge(
                    'flex h-10 w-full items-center gap-2 rounded bg-gray-200 px-2',
                    className,
                )}
            >
                {/*
                 * One toggle button, named once and carrying its state in
                 * aria-pressed, as in AudioPlayer: a screen reader announces
                 * "Play, pressed" while playing. The title used to be the only
                 * name, and it was dropped while playing.
                 */}
                <BaseButton
                    type="button"
                    onClick={togglePlayPause}
                    className="size-6 shrink-0 cursor-pointer p-0"
                    title={
                        isPlaying ? undefined : i18next.t('Tooltip.Audio.Play')
                    }
                    aria-label={i18next.t('Tooltip.Audio.Play')}
                    aria-pressed={isPlaying}
                    aria-describedby={playDescribedBy}
                >
                    <Icon
                        iconKey={
                            isPlaying
                                ? 'pause_circle_outline'
                                : 'play_circle_outline'
                        }
                        size="1.5rem"
                        className="text-gray-700"
                    />
                </BaseButton>
                {/*
                 * A native range input, so dragging, touch and the screen
                 * reader adjust gestures come for free. Driven in seconds,
                 * not percent: one arrow press is one second on a 40-minute
                 * recording as well as on a 7-second voicemail, and the value
                 * the browser announces means something on its own.
                 */}
                <RangeSlider
                    handleChange={(e) =>
                        seekToSeconds(parseFloat(e.target.value))
                    }
                    preferredValue={Math.round(elapsed)}
                    min={0}
                    max={Math.round(totalDuration)}
                    step={1}
                    ariaLabel={i18next.t('audio.player.position')}
                    ariaValueText={`${formatTime(elapsed) || '00:00'} / ${
                        formatTime(totalDuration) || '00:00'
                    }`}
                />
                <div className="min-w-10 shrink-0 text-center text-xs text-gray-500 tabular-nums antialiased">
                    {formatTime(timeLeft)}
                </div>
            </div>
            {presentDownload ? (
                <Tooltip label={i18next.t('Tooltip.Audio.Download')}>
                    <a
                        href={downloadUrl || url}
                        download={true}
                        tabIndex={rowTabIndex}
                        onClick={onDownloadHrefClicked}
                        aria-label={i18next.t('Tooltip.Audio.Download')}
                        className="hover:darken-10 flex cursor-pointer items-center justify-center rounded-sm border-none bg-gray-200 p-1"
                    >
                        <Icon iconKey="get_app" />
                    </a>
                </Tooltip>
            ) : null}
            <audio ref={audioRef} src={url} preload={preload} />
        </div>
    );
};

export default AudioPlayerV2;
